/**
 * Prepares the photo to upload on the CLIENT: centered square crop and
 * resizing.
 *
 * It does not replace server-side normalization—which is authoritative, strips
 * EXIF, and guarantees the format—but avoids uploading 5 MB from a phone just for
 * the server to discard it. A 12 MP photo goes from ~4 MB to ~40 KB before
 * leaving the device.
 *
 * If anything fails (browser without canvas, image that does not decode), it returns the
 * ORIGINAL file: the server will normalize it anyway. Preparation is an
 * optimization, not a requirement.
 */

/** Side of the uploaded square. Larger than the server's 256, so
 *  the server can resize with a better filter without losing sharpness. */
const CLIENT_AVATAR_SIZE = 512;

/** Maximum size accepted by the server (`MAX_INPUT_BYTES` in Rust). */
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/** Types the server can decode. */
export const ACCEPTED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export type AvatarFileError = "too-large" | "unsupported-type";

/**
 * Performs every check possible without decoding.
 *
 * This only provides a USEFUL message before upload: the browser sets a `File`'s
 * `type` from the extension and it can be spoofed, so the server performs
 * the real validation (magic bytes).
 */
export function validateAvatarFile(file: File): AvatarFileError | null {
  if (file.size > MAX_AVATAR_BYTES) {
    return "too-large";
  }
  if (file.type && !ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    return "unsupported-type";
  }
  return null;
}

/** Centered square crop: the largest box that fits in the image. */
export function centerSquare(width: number, height: number) {
  const side = Math.min(width, height);
  return {
    side,
    x: Math.floor((width - side) / 2),
    y: Math.floor((height - side) / 2),
  };
}

/**
 * Returns the file ready for upload. It never throws: on any problem,
 * it returns the original.
 */
export async function prepareAvatarFile(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const { side, x, y } = centerSquare(bitmap.width, bitmap.height);

    const canvas = document.createElement("canvas");
    canvas.width = CLIENT_AVATAR_SIZE;
    canvas.height = CLIENT_AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return file;
    }
    ctx.drawImage(bitmap, x, y, side, side, 0, 0, CLIENT_AVATAR_SIZE, CLIENT_AVATAR_SIZE);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", 0.9);
    });
    if (!blob || blob.size === 0) {
      return file;
    }
    return new File([blob], "avatar.webp", { type: "image/webp" });
  } catch {
    // Browser without `createImageBitmap`, corrupt image, tainted canvas…
    // The server still normalizes it; uploading the original is worse but works.
    return file;
  }
}
